import {
  createCorporateOnlyLessonDefinition,
  editCorporateOnlyLessonDefinitionDetail,
  getCorporateOnlyLessonDefinitionDetail,
} from '../../../api/corporateOnlyLessonDefinitionApi'
import { getStudiosSuggestion, getStudiosSuggestionsByIds } from '../../../api/studiosApi'
import DebounceSelect from '../../../components/debounce-select/debounce-select'
import { keysToCamel } from '../../../helpers/keys-to-case'
import { getDayOfWeekLabel, getOptions } from '../../../helpers/utils'
import translate from '../../../i18n'
import {
  CorporateOnlyLessonDefinition,
  CreateCorporateOnlyLessonDefinitionFormData,
} from '../share-type'
import { Button, Checkbox, Form, notification, Space, TimePicker } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const CorporateOnlyLessonDefinitionForm = () => {
  const navigate = useNavigate()
  const params = useParams()

  const [form] = Form.useForm()
  const [loading, setLoading] = useState<boolean>(false)

  const [defaultStudioSuggestionsOptions, setStudioSuggestionsOptions] = useState<
    { label: string; value: number }[]
  >([])

  const corporateOnlyLessonDefinitionId = params?.id

  useEffect(() => {
    if (corporateOnlyLessonDefinitionId) {
      getCorporateOnlyLessonDefinitionDetail(Number(corporateOnlyLessonDefinitionId)).then(
        (res: any) => {
          const { studio, timeRangeStart, timeRangeEnd, ...other } = keysToCamel(
            res,
          ) as CorporateOnlyLessonDefinition
          const corporateOnlyLessonDefinitionForm = {
            ...other,
            studioId: studio.id,
            timeRange: [
              timeRangeStart ? dayjs(timeRangeStart, 'HH:mm:ss') : undefined,
              timeRangeEnd ? dayjs(timeRangeEnd, 'HH:mm:ss') : undefined,
            ],
          }

          form.setFieldsValue(corporateOnlyLessonDefinitionForm)
          if (studio) {
            setStudioSuggestionsOptionsAction(studio.id)
          }
        },
      )
    }
  }, [corporateOnlyLessonDefinitionId])

  const onFinish = (formData: CreateCorporateOnlyLessonDefinitionFormData) => {
    const { timeRange, daysOfWeek, ...other } = formData

    const bodyFormData = {
      ...other,
      daysOfWeek: daysOfWeek || [],
      timeRangeStart: timeRange ? timeRange[0]?.format('HH:mm:ss') : undefined,
      timeRangeEnd: timeRange ? timeRange[1]?.format('HH:mm:ss') : undefined,
    }

    setLoading(true)

    let promise
    if (corporateOnlyLessonDefinitionId) {
      promise = editCorporateOnlyLessonDefinitionDetail(
        Number(corporateOnlyLessonDefinitionId),
        bodyFormData,
      ).then(res => {
        notification.success({
          message: translate('corporateOnlyLessonDefinitions.updateSuccess'),
        })
        navigate(`/corporate-only-lesson-definitions/${res.id}`)
      })
    } else {
      promise = createCorporateOnlyLessonDefinition(bodyFormData).then(res => {
        notification.success({
          message: translate('corporateOnlyLessonDefinitions.createSuccess'),
        })
        navigate(`/corporate-only-lesson-definitions/${res.id}`)
      })
    }
    promise
      .catch(error => {
        notification.error({
          message: translate('apiError'),
          description: error.message ?? '',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const goBack = () => {
    navigate('/corporate-only-lesson-definitions')
  }

  async function fetchStudioList(keyword: string): Promise<{ label: string; value: number }[]> {
    return getStudiosSuggestion(keyword).then(res => {
      return getOptions(res)
    })
  }
  const setStudioSuggestionsOptionsAction = (studioId: number) => {
    getStudiosSuggestionsByIds([studioId]).then(res => {
      setStudioSuggestionsOptions(getOptions(res))
    })
  }

  return (
    <Form
      name="corporate-only-lesson-definition"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 12 }}
      autoComplete="off"
      form={form}
      onFinish={onFinish}
    >
      <Form.Item
        label={translate('corporateOnlyLessonDefinitions.studioId')}
        name="studioId"
        rules={[
          {
            required: true,
            message: translate('corporateOnlyLessonDefinitions.studioIdRuleMessage'),
          },
        ]}
      >
        <DebounceSelect
          style={{ minWidth: 130 }}
          showSearch
          allowClear
          placeholder={translate('searchTrends.studio.message')}
          fetchOptions={fetchStudioList}
          defaultOptions={defaultStudioSuggestionsOptions}
          disabled={corporateOnlyLessonDefinitionId != undefined}
        />
      </Form.Item>
      <Form.Item label={translate('corporateOnlyLessonDefinitions.daysOfWeek')} name="daysOfWeek">
        <Checkbox.Group>
          {Array.from({ length: 7 }, (_, i) => (
            <Checkbox key={i + 1} value={i + 1}>
              {getDayOfWeekLabel(i + 1)}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>
      <Form.Item label={translate('corporateOnlyLessonDefinitions.timeRange')} name="timeRange">
        <TimePicker.RangePicker format="HH:mm:ss" />
      </Form.Item>
      <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
        <Space size={16}>
          <Button loading={loading} type="primary" htmlType="submit">
            {translate('submit')}
          </Button>

          <Button htmlType="button" onClick={goBack}>
            {translate('goBack')}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  )
}
export default () => <CorporateOnlyLessonDefinitionForm />
